import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, CheckCircle, AlertTriangle, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import ThreeDPreview from './ThreeDPreview';
import DxfParser from 'dxf-parser';

const CAD_EXTENSIONS = ['.stl', '.obj', '.3mf', '.ply', '.amf', '.step', '.stp'];
const DXF_EXTENSIONS = ['.dxf'];
const DWG_EXTENSIONS = ['.dwg'];
const PDF_EXTENSIONS = ['.pdf'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

const getFileExtension = (filename) => {
  const ext = filename?.toLowerCase().match(/\.[^.]+$/);
  return ext ? ext[0] : '';
};

const getFileType = (file) => {
  if (!file) return 'unknown';
  const ext = getFileExtension(file.name);
  if (CAD_EXTENSIONS.includes(ext)) return 'cad';
  if (DXF_EXTENSIONS.includes(ext)) return 'dxf';
  if (DWG_EXTENSIONS.includes(ext)) return 'dwg';
  if (PDF_EXTENSIONS.includes(ext)) return 'pdf';
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  return 'unknown';
};

/* ---------- DXF Canvas Renderer ---------- */
const acColorIndex = [
  '#000000','#FF0000','#FFFF00','#00FF00','#00FFFF','#0000FF','#FF00FF','#FFFFFF',
  '#808080','#C0C0C0','#FF0000','#FF7F7F','#CC0000','#CC6666','#990000','#994C4C',
  '#7F0000','#7F3F3F','#4C0000','#4C2626','#FF3F00','#FF9F7F','#CC3200','#CC7F66',
  '#993200','#995F4C','#7F2900','#7F4F3F','#4C1900','#4C2F26','#FF7F00','#FFBF7F',
];

const resolveColor = (entity, dxf) => {
  if (entity.color != null && entity.color > 0 && entity.color < 256) {
    return acColorIndex[entity.color] || '#888888';
  }
  if (entity.layer && dxf.tables?.layer?.layers?.[entity.layer]) {
    const layer = dxf.tables.layer.layers[entity.layer];
    if (layer.color != null && layer.color > 0) {
      return acColorIndex[layer.color] || '#888888';
    }
  }
  return '#888888';
};

const renderDxfToCanvas = (canvas, dxf, bgColor = '#f8fafc') => {
  const ctx = canvas.getContext('2d');
  const entities = dxf.entities || [];
  if (entities.length === 0) return;

  // Calculate bounds
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const processPoint = (x, y) => {
    if (x != null && y != null) {
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    }
  };

  for (const e of entities) {
    if (e.type === 'LINE') {
      processPoint(e.vertices?.[0]?.x, e.vertices?.[0]?.y);
      processPoint(e.vertices?.[1]?.x, e.vertices?.[1]?.y);
    } else if (e.type === 'LWPOLYLINE' || e.type === 'POLYLINE') {
      (e.vertices || []).forEach(v => processPoint(v.x, v.y));
    } else if (e.type === 'CIRCLE' || e.type === 'ARC') {
      processPoint(e.center?.x - (e.radius || 0), e.center?.y - (e.radius || 0));
      processPoint(e.center?.x + (e.radius || 0), e.center?.y + (e.radius || 0));
    } else if (e.type === 'ELLIPSE') {
      const mx = Math.abs(e.majorAxisEndPoint?.x || 0) + Math.abs(e.majorAxisEndPoint?.y || 0);
      processPoint(e.center?.x - mx, e.center?.y - mx);
      processPoint(e.center?.x + mx, e.center?.y + mx);
    } else if (e.type === 'SPLINE') {
      (e.controlPoints || []).forEach(v => processPoint(v.x, v.y));
    } else if (e.type === 'POINT') {
      processPoint(e.position?.x, e.position?.y);
    } else if (e.type === 'INSERT') {
      processPoint(e.position?.x, e.position?.y);
    } else if (e.type === 'SOLID' || e.type === '3DFACE') {
      (e.points || []).forEach(v => processPoint(v.x, v.y));
    }
  }

  if (!isFinite(minX)) return;

  const padding = 30;
  const w = canvas.width - padding * 2;
  const h = canvas.height - padding * 2;
  const dxfW = maxX - minX || 1;
  const dxfH = maxY - minY || 1;
  const scale = Math.min(w / dxfW, h / dxfH);
  const offsetX = padding + (w - dxfW * scale) / 2;
  const offsetY = padding + (h - dxfH * scale) / 2;

  const tx = (x) => offsetX + (x - minX) * scale;
  const ty = (y) => canvas.height - (offsetY + (y - minY) * scale); // flip Y

  // Clear
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw entities
  for (const e of entities) {
    const color = resolveColor(e, dxf);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1;

    if (e.type === 'LINE' && e.vertices?.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(tx(e.vertices[0].x), ty(e.vertices[0].y));
      ctx.lineTo(tx(e.vertices[1].x), ty(e.vertices[1].y));
      ctx.stroke();
    } else if ((e.type === 'LWPOLYLINE' || e.type === 'POLYLINE') && e.vertices?.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(tx(e.vertices[0].x), ty(e.vertices[0].y));
      for (let i = 1; i < e.vertices.length; i++) {
        ctx.lineTo(tx(e.vertices[i].x), ty(e.vertices[i].y));
      }
      if (e.shape) ctx.closePath();
      ctx.stroke();
    } else if (e.type === 'CIRCLE' && e.center && e.radius) {
      ctx.beginPath();
      ctx.arc(tx(e.center.x), ty(e.center.y), e.radius * scale, 0, Math.PI * 2);
      ctx.stroke();
    } else if (e.type === 'ARC' && e.center && e.radius) {
      const startAngle = -(e.endAngle || 0) * Math.PI / 180;
      const endAngle = -(e.startAngle || 0) * Math.PI / 180;
      ctx.beginPath();
      ctx.arc(tx(e.center.x), ty(e.center.y), e.radius * scale, startAngle, endAngle);
      ctx.stroke();
    } else if (e.type === 'ELLIPSE' && e.center && e.majorAxisEndPoint) {
      const mx = e.majorAxisEndPoint.x || 0;
      const my = e.majorAxisEndPoint.y || 0;
      const major = Math.sqrt(mx * mx + my * my) * scale;
      const minor = major * (e.axisRatio || 1);
      const rotation = Math.atan2(my, mx);
      ctx.beginPath();
      ctx.ellipse(tx(e.center.x), ty(e.center.y), major, minor, -rotation, 0, Math.PI * 2);
      ctx.stroke();
    } else if (e.type === 'SPLINE' && e.controlPoints?.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(tx(e.controlPoints[0].x), ty(e.controlPoints[0].y));
      for (let i = 1; i < e.controlPoints.length; i++) {
        ctx.lineTo(tx(e.controlPoints[i].x), ty(e.controlPoints[i].y));
      }
      ctx.stroke();
    } else if (e.type === 'POINT' && e.position) {
      ctx.beginPath();
      ctx.arc(tx(e.position.x), ty(e.position.y), 2, 0, Math.PI * 2);
      ctx.fill();
    } else if ((e.type === 'SOLID' || e.type === '3DFACE') && e.points?.length >= 3) {
      ctx.beginPath();
      ctx.moveTo(tx(e.points[0].x), ty(e.points[0].y));
      for (let i = 1; i < e.points.length; i++) {
        ctx.lineTo(tx(e.points[i].x), ty(e.points[i].y));
      }
      ctx.closePath();
      ctx.globalAlpha = 0.3;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.stroke();
    }
  }
};

// DXF Viewer sub-component
const DxfPreview = ({ file }) => {
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityCount, setEntityCount] = useState(0);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;

    const loadDxf = async () => {
      try {
        setLoading(true);
        setError(null);

        const text = await file.text();
        if (cancelled) return;

        const parser = new DxfParser();
        const dxf = parser.parseSync(text);
        if (cancelled) return;

        if (!dxf || !dxf.entities || dxf.entities.length === 0) {
          setError('No drawable entities found in DXF file');
          setLoading(false);
          return;
        }

        setEntityCount(dxf.entities.length);

        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set high-DPI canvas
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.parentElement.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = 350 * dpr;
        canvas.style.width = rect.width + 'px';
        canvas.style.height = '350px';
        canvas.getContext('2d').scale(dpr, dpr);

        // Use logical size for rendering
        const logicalCanvas = { 
          width: rect.width, height: 350, 
          getContext: () => canvas.getContext('2d')
        };
        renderDxfToCanvas(logicalCanvas, dxf, '#f8fafc');

        setLoading(false);
      } catch (err) {
        console.error('DXF parse error:', err);
        if (!cancelled) {
          setError('Could not parse DXF file');
          setLoading(false);
        }
      }
    };

    loadDxf();
    return () => { cancelled = true; };
  }, [file]);

  if (error) {
    return (
      <div className="w-full min-h-[200px] rounded-xl bg-amber-50/50 border-2 border-amber-200 flex flex-col items-center justify-center gap-2 p-6">
        <AlertTriangle className="w-8 h-8 text-amber-500" />
        <p className="text-sm font-medium text-slate-700">{error}</p>
        <p className="text-xs text-slate-500">File uploaded successfully</p>
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-xl overflow-hidden bg-slate-50 border border-slate-200">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-100/80 z-10">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-[#0007b5] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-slate-500 font-medium">Parsing DXF file...</p>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="w-full" style={{ height: '350px' }} />
      {!loading && entityCount > 0 && (
        <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs text-slate-500 border border-slate-200">
          {entityCount} entities
        </div>
      )}
    </div>
  );
};

// PDF Viewer sub-component
const PdfPreview = ({ file }) => {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!objectUrl) return null;

  return (
    <div className="w-full rounded-xl overflow-hidden border border-slate-200">
      <iframe
        src={objectUrl}
        title="PDF Preview"
        className="w-full h-[400px]"
        style={{ border: 'none' }}
      />
    </div>
  );
};

// Image Viewer sub-component
const ImagePreview = ({ file }) => {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!objectUrl) return null;

  return (
    <div className="w-full rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center p-4">
      <img
        src={objectUrl}
        alt={file.name}
        className="max-w-full max-h-[400px] rounded-lg object-contain"
      />
    </div>
  );
};

// DWG placeholder
const DwgPlaceholder = () => (
  <div className="w-full min-h-[200px] rounded-xl bg-emerald-50/50 border-2 border-emerald-200 flex flex-col items-center justify-center gap-3 p-6">
    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
      <CheckCircle className="w-6 h-6 text-emerald-600" />
    </div>
    <p className="text-sm font-semibold text-slate-900">File uploaded successfully</p>
    <p className="text-xs text-slate-500 text-center">
      DWG preview is not available, but your file has been uploaded and will be reviewed by our team.
    </p>
  </div>
);

// Unknown file placeholder
const UnknownPlaceholder = () => (
  <div className="w-full min-h-[200px] rounded-xl bg-emerald-50/50 border-2 border-emerald-200 flex flex-col items-center justify-center gap-3 p-6">
    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
      <CheckCircle className="w-6 h-6 text-emerald-600" />
    </div>
    <p className="text-sm font-semibold text-slate-900">File uploaded successfully</p>
    <p className="text-xs text-slate-500 text-center">
      Preview is not available for this file type, but your file has been uploaded.
    </p>
  </div>
);

// Main FilePreview component
const FilePreview = ({ file, material, strength, quality, units, onAnalysis, onFileModified }) => {
  const fileType = getFileType(file);

  switch (fileType) {
    case 'cad':
      return (
        <ThreeDPreview
          file={file}
          material={material}
          strength={strength}
          quality={quality}
          units={units}
          onAnalysis={onAnalysis}
          onFileModified={onFileModified}
        />
      );
    case 'dxf':
      return <DxfPreview file={file} />;
    case 'dwg':
      return <DwgPlaceholder />;
    case 'pdf':
      return <PdfPreview file={file} />;
    case 'image':
      return <ImagePreview file={file} />;
    default:
      return <UnknownPlaceholder />;
  }
};

export default FilePreview;
